import React, { useState } from "react";
import { hasOwnProperty } from "maven-lib/dist/utils/misc";
import { CrButton } from "./CrButton";
import { CrIconButton } from "./CrIconButton";
import { CrYearMonth } from "./CrYearMonth";
import dayjs from "dayjs";

export interface CrCalendarDateLabel {
  year?: string;
  month?: string;
}

export interface CrYearMonthExtProps {
  selectedYearMonth?: { year?: number; month?: number };
  minDate?: Date | number | "TODAY";
  maxDate: Date | number | "TODAY";
  allUnknownable: boolean;
  yearUnknownable: boolean;
  monthUnknownable: boolean;
  dateLabel?: CrCalendarDateLabel;
  format?: "YYYY/MM" | "YYYY-MM" | "MMM/YYYY";
  onValueChange: (val: string) => void;
  onDeleteDate: () => void;
  onDismiss?: () => void;
}

const generateUnknowableDateFormats = (
  date: Date,
  format?: "YYYY/MM" | "YYYY-MM" | "MMM/YYYY",
  unknowable?: {
    allUnknownable?: boolean;
    yearUnknownable?: boolean;
    monthUnknownable?: boolean;
  }
): string => {
  if (format === "MMM/YYYY") {
    if (!!unknowable?.allUnknownable) {
      return "UNK/UKUK";
    }
    if (!!unknowable?.monthUnknownable) {
      return `${dayjs(date).format("UNK/YYYY")}`;
    }
    if (!!unknowable?.yearUnknownable) {
      return `${dayjs(date).format("MMM/UKUK")}`;
    }
  }

  if (format === "YYYY/MM" || format === "YYYY-MM") {
    if (!!unknowable?.allUnknownable) {
      return "UKUK-UK";
    }
    if (!!unknowable?.monthUnknownable) {
      return `${dayjs(date).format("YYYY-UK")}`;
    }
    if (!!unknowable?.yearUnknownable) {
      return `${dayjs(date).format("UKUK-MM")}`;
    }
  }

  return "";
};

const getCurrentSelectableMonthValue = (format?: "YYYY/MM" | "YYYY-MM" | "MMM/YYYY") => {
  if (format === "MMM/YYYY") {
    return `${dayjs().format("MMM/YYYY")}`;
  }

  return `${dayjs().format("YYYY-MM")}`;
};

export function CrYearMonthExt(props: CrYearMonthExtProps) {
  const { selectedYearMonth, minDate, maxDate, onValueChange, allUnknownable, yearUnknownable, monthUnknownable, format, onDeleteDate } =
    props;

  const [currentDate, setCurrentDate] = useState<Date>(
    ((v) => {
      if (v && (hasOwnProperty(v, "year") || hasOwnProperty(v, "month")))
        return dayjs()
          .set("year", v.year || dayjs().year())
          .set("month", typeof v.month === "number" ? v.month - 1 : dayjs().month())
          .set("day", dayjs().day())
          .toDate();
      else return new Date();
    })(selectedYearMonth)
  );

  const curMonthSelectable = (() => {
    const maxVal =
      maxDate instanceof Date
        ? dayjs(maxDate.getTime()).format("YYYYMM")
        : typeof maxDate === "number"
        ? dayjs().add(maxDate, "days").format("YYYYMM")
        : dayjs().format("YYYYMM");

    const minVal =
      minDate instanceof Date
        ? dayjs(minDate.getTime()).format("YYYYMM")
        : typeof minDate === "number"
        ? dayjs().subtract(minDate, "days").format("YYYYMM")
        : dayjs().format("YYYYMM");

    return maxVal >= dayjs().format("YYYYMM") && minVal <= dayjs().format("YYYYMM");
  })();

  return (
    <div className="cr-calendar-ext">
      <CrYearMonth
        onSelectDate={(date) => onValueChange(dayjs(date).format("YYYYMM"))}
        onYearChange={(date) => setCurrentDate(date)}
        onMonthChange={(date) => setCurrentDate(date)}
        {...props}
      />
      <div className="cr-calendar-uk">
        {curMonthSelectable && (
          <CrButton onClick={() => onValueChange(dayjs().format("YYYYMM"))}>
            This Month {` (${getCurrentSelectableMonthValue(format)})`}
          </CrButton>
        )}
        {allUnknownable && (
          <CrButton onClick={() => onValueChange("UKUKUK")}>
            {generateUnknowableDateFormats(currentDate, format, { allUnknownable })}
          </CrButton>
        )}
        {yearUnknownable && (
          <CrButton onClick={() => onValueChange(dayjs(currentDate).format("UKUKMM"))}>
            {generateUnknowableDateFormats(currentDate, format, { yearUnknownable })}
          </CrButton>
        )}
        {monthUnknownable && (
          <CrButton onClick={() => onValueChange(dayjs(currentDate).format("YYYYUK"))}>
            {generateUnknowableDateFormats(currentDate, format, { monthUnknownable })}
          </CrButton>
        )}
        <CrIconButton color="inner-bordered" onClick={() => onDeleteDate && onDeleteDate()} icon="EraseTool" />
      </div>
    </div>
  );
}

CrYearMonthExt.defaultProps = {
  allUnknownable: false,
  yearUnknownable: false,
  monthUnknownable: false,
};
